import { StepFilter, ApplicantStageFilter } from 'types/graphql'

import {
  ApplicantStage,
  DisqualifiedReason,
  HiringStage,
  NotInterestedReason,
} from 'src/graphql/types/applicants'
import {
  EmploymentEquipmentType,
  formatEmploymentEquipmentType,
} from 'src/graphql/types/employmentExperiences'
import {
  Endorsement,
  EndorsementDescriptions,
} from 'src/graphql/types/licenses'
import { StepType, StepStatus } from 'src/graphql/types/steps'
import { formatSnakeValue, formatStepType } from 'src/lib/formatters'
import { TERMINATED_REASON_OPTIONS } from 'src/lib/terminatedReason.utils'
import { STATE_AND_PROVINCE_ABBREVIATIONS } from 'src/lib/usStates'

import { TASK_DEADLINE_OPTIONS } from '../FilterMenu/utils/taskFilterOptions'
import {
  MultiSelectFilter,
  Option,
} from '../MultiSelectFilter/MultiSelectFilter'

export interface Filters {
  applicantStages?: ApplicantStageFilters
  yearsOfExperience?: RangeFilter
  callCount?: RangeFilter
  missedOutboundCallCount?: RangeFilter
  recruiters?: MultiSelectFilter
  jobListings?: MultiSelectFilter
  leadSources?: MultiSelectFilter
  endorsements?: MultiSelectFilter
  states?: MultiSelectFilter
  tags?: MultiSelectFilter
  equipmentTypes?: MultiSelectFilter
  eligibleForHire?: MultiSelectFilter
  permanentlyDisqualified?: MultiSelectFilter
  taskDeadline?: MultiSelectFilter
  taskDueDate?: DateRangeFilter
  taskAssignees?: MultiSelectFilter
  taskTemplates?: MultiSelectFilter
  searchText?: SearchFilter
  applicationDate?: DateRangeFilter
  lastContactDate?: DateRangeFilter
  availableForWorkDate?: DateRangeFilter
  hiredDate?: DateRangeFilter
  terminatedDate?: DateRangeFilter
  disqualifiedDate?: DateRangeFilter
  notInterestedDate?: DateRangeFilter
  bookingDate?: DateRangeFilter
  steps?: StepFilters
  zipCodes?: ZipcodesFilterType
  customFields?: any[]
}

export interface Filter {
  type:
    | 'multi-select'
    | 'range'
    | 'search'
    | 'date-range'
    | 'step-filter'
    | 'applicant-stage-filter'
    | 'location'
  key: string
  title?: string
}

export interface MultiSelectFilter extends Filter {
  options: Option[]
  selectedValues?: string[]
}

export interface SearchFilter extends Filter {
  searchText: string
}

export interface RangeFilter extends Filter {
  lte: number
  gte: number
  min: number
  max: number
}

export interface DateRangeFilter extends Filter {
  lte: Date
  gte: Date
}

export interface MultiOption extends Option {
  options: Option[]
}

export interface StepFilters extends Filter {
  options: MultiOption[]
  selectedValues?: StepFilter[]
}

export interface ApplicantStageFilters extends Filter {
  options: MultiOption[]
  selectedValues?: ApplicantStageFilter[]
}

export interface ZipcodesFilterType extends Filter {
  values: string[]
}

export const STATE_ABBREVIATIONS = STATE_AND_PROVINCE_ABBREVIATIONS

export const defaultFilters: Filters = {
  applicantStages: {
    type: 'applicant-stage-filter',
    title: 'Applicant Stage',
    key: 'applicantStages',
    selectedValues: [],
    options: Object.values(ApplicantStage).map((applicantStage) => {
      if (applicantStage === ApplicantStage.PROSPECT) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: Object.values(HiringStage).map((hiringStage) => ({
            value: hiringStage,
            label: formatSnakeValue(hiringStage),
            selected: false,
          })),
        }
      }
      if (applicantStage === ApplicantStage.HIRED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: [
            {
              value: 'ALL',
              label: 'All',
              selected: false,
            },
          ],
        }
      }
      if (applicantStage === ApplicantStage.DISQUALIFIED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: Object.values(DisqualifiedReason).map((reason) => ({
            value: reason,
            label: formatSnakeValue(reason),
            selected: false,
          })),
        }
      }
      if (applicantStage === ApplicantStage.NOT_INTERESTED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: Object.values(NotInterestedReason).map((reason) => ({
            value: reason,
            label: formatSnakeValue(reason),
            selected: false,
          })),
        }
      }
      if (applicantStage === ApplicantStage.TERMINATED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: TERMINATED_REASON_OPTIONS.map((option) => ({
            ...option,
            selected: false,
          })),
        }
      }
    }),
  } as ApplicantStageFilters,
  yearsOfExperience: {
    type: 'range',
    title: 'Experience',
    min: 0,
    max: 30,
    lte: null,
    gte: null,
    key: 'yearsOfExperience',
  } as RangeFilter,
  callCount: {
    type: 'range',
    title: 'Call count',
    min: 0,
    max: 20,
    lte: null,
    gte: null,
    key: 'callCount',
  } as RangeFilter,
  missedOutboundCallCount: {
    type: 'range',
    title: 'Missed call count',
    min: 0,
    max: 20,
    lte: null,
    gte: null,
    key: 'missedOutboundCallCount',
  } as RangeFilter,
  recruiters: {
    type: 'multi-select',
    title: 'Employee',
    options: [],
    key: 'recruiters',
  } as MultiSelectFilter,
  jobListings: {
    type: 'multi-select',
    title: 'Position',
    options: [],
    key: 'jobListings',
  } as MultiSelectFilter,
  leadSources: {
    type: 'multi-select',
    title: 'Lead Source',
    // Options are loaded dynamically (in-use link types + their names) by
    // useLeadSourcesFilter; the stored filter only tracks selected values.
    options: [],
    key: 'leadSources',
    selectedValues: [],
  } as MultiSelectFilter,
  endorsements: {
    type: 'multi-select',
    title: 'Endorsements',
    options: Object.values(Endorsement).map((endorsement) => ({
      value: endorsement,
      label: `${endorsement} - ${EndorsementDescriptions[endorsement]}`,
      selected: false,
    })),
    key: 'endorsements',
  } as MultiSelectFilter,
  states: {
    type: 'multi-select',
    title: 'States',
    options: STATE_ABBREVIATIONS.map((state) => ({
      value: state,
      label: state,
      selected: false,
    })),
    key: 'states',
  } as MultiSelectFilter,
  tags: {
    type: 'multi-select',
    title: 'Tags',
    options: [],
    key: 'tags',
  } as MultiSelectFilter,
  equipmentTypes: {
    type: 'multi-select',
    title: 'Equipment Types',
    options: Object.values(EmploymentEquipmentType).map((equipmentType) => ({
      value: equipmentType,
      label: formatEmploymentEquipmentType(equipmentType),
      selected: false,
    })),
    key: 'equipmentTypes',
  } as MultiSelectFilter,
  eligibleForHire: {
    type: 'multi-select',
    title: 'Eligible for Hire',
    options: [
      { value: 'true', label: 'Yes', selected: false },
      { value: 'false', label: 'No', selected: false },
    ],
    key: 'eligibleForHire',
    selectedValues: [],
  } as MultiSelectFilter,
  permanentlyDisqualified: {
    type: 'multi-select',
    title: 'Permanently Disqualified',
    options: [
      { value: 'true', label: 'Yes', selected: false },
      { value: 'false', label: 'No', selected: false },
    ],
    key: 'permanentlyDisqualified',
    selectedValues: [],
  } as MultiSelectFilter,
  searchText: {
    type: 'search',
    key: 'searchText',
    searchText: '',
  } as SearchFilter,
  applicationDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Applied Date',
    key: 'applicationDate',
  } as DateRangeFilter,
  taskDeadline: {
    type: 'multi-select',
    title: 'Task Deadline',
    options: TASK_DEADLINE_OPTIONS.map((o) => ({ ...o, selected: false })),
    key: 'taskDeadline',
    selectedValues: [],
  } as MultiSelectFilter,
  taskDueDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Task Deadline',
    key: 'taskDueDate',
  } as DateRangeFilter,
  taskAssignees: {
    type: 'multi-select',
    title: 'Task Assignees',
    options: [],
    key: 'taskAssignees',
    selectedValues: [],
  } as MultiSelectFilter,
  taskTemplates: {
    type: 'multi-select',
    title: 'Task Template',
    options: [],
    key: 'taskTemplates',
    selectedValues: [],
  } as MultiSelectFilter,
  lastContactDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Last Contact Date',
    key: 'lastContactDate',
  } as DateRangeFilter,
  availableForWorkDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Available for Work Date',
    key: 'availableForWorkDate',
  } as DateRangeFilter,
  hiredDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Hired Date',
    key: 'hiredDate',
  } as DateRangeFilter,
  terminatedDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Terminated Date',
    key: 'terminatedDate',
  } as DateRangeFilter,
  disqualifiedDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Disqualified Date',
    key: 'disqualifiedDate',
  } as DateRangeFilter,
  notInterestedDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Not Interested Date',
    key: 'notInterestedDate',
  } as DateRangeFilter,
  bookingDate: {
    type: 'date-range',
    lte: null,
    gte: null,
    title: 'Booking Date',
    key: 'bookingDate',
  } as DateRangeFilter,
  steps: {
    type: 'step-filter',
    title: 'Steps',
    key: 'steps',
    options: Object.values(StepType).map((type) => ({
      value: type,
      label: formatStepType(type),
      selected: false,
      options: Object.values(StepStatus).map((status) => ({
        value: status,
        label: formatSnakeValue(status),
        selected: false,
      })),
    })),
  } as StepFilters,
  zipCodes: {
    type: 'location',
    title: 'Location',
    key: 'zipCodes',
    values: [],
  } as ZipcodesFilterType,
  customFields: [],
}
